/**
 * PostHog analytics layer for the Pearl Vendor App.
 *
 * Mirrors the structure used by the Pearl Planner app so event names,
 * property shapes, and dedupe semantics line up across both apps and we can
 * union them in PostHog without renormalizing.
 *
 * Gating rules (intentional):
 *   - Disabled in tests (NODE_ENV === 'test')
 *   - Disabled if REACT_APP_POSTHOG_KEY is missing (no-op, no warnings)
 *   - In non-production builds, additionally requires
 *     REACT_APP_POSTHOG_ENABLE_ANALYTICS=true so local dev doesn't pollute
 *     the production project. When that flag is off, calls log to console
 *     with a [Analytics - Dev] prefix instead.
 */

import posthog from 'posthog-js';

const ANALYTICS_EVENTS = Object.freeze({
    VENDOR_VIEWED: 'vendor_viewed',
    VENDOR_PROFILE_VIEWED: 'vendor_profile_viewed',
    // Pearl marketing landing page (route '/'), instrumented ahead of a
    // redesign to learn scroll depth and which feature CTAs convert.
    LANDING_PAGE_VIEWED: 'landing_page_viewed',
    LANDING_PAGE_SCROLLED: 'landing_page_scrolled',
    LANDING_CTA_CLICKED: 'landing_cta_clicked',
});

const VENDOR_VIEW_SOURCES = Object.freeze({
    SEARCH: 'search',
    RECOMMENDATIONS: 'recommendations',
    SAVED_VENDORS: 'saved_vendors',
    MAP: 'map',
    DIRECTORY_NATIONAL: 'directory_national',
    DIRECTORY_LOCAL: 'directory_local',
    // Origin when there's no in-app referrer: SEO/direct entry, refresh, or
    // an external link straight to a profile.
    DIRECT: 'direct',
});

/**
 * Canonicalize the many shapes vendor_category arrives in so category
 * breakdowns in PostHog don't split into near-duplicate values.
 *
 * Handles: a plain string ("Florist"), a comma-joined string
 * ("Florist, DJ" — how claimed webstores store multi-category), a real
 * array (["Florist", "DJ"]), and a JSON-stringified array ('["Florist"]'
 * — how some search-result records carry categoryDetails.name).
 *
 * Returns { primary, all }: primary is the first canonical category (a
 * single string for clean breakdowns), all is the full list. Never throws —
 * it runs inside the analytics path and must not break product flows.
 */
const normalizeVendorCategory = (raw) => {
    try {
        if (raw === null || raw === undefined) return { primary: undefined, all: [] };

        let list;
        if (Array.isArray(raw)) {
            list = raw;
        } else if (typeof raw === 'string') {
            const trimmed = raw.trim();
            if (trimmed === '') return { primary: undefined, all: [] };
            if (trimmed.startsWith('[')) {
                try {
                    const parsed = JSON.parse(trimmed);
                    list = Array.isArray(parsed) ? parsed : [trimmed];
                } catch {
                    // Malformed JSON-ish string — treat as a plain value.
                    list = [trimmed];
                }
            } else {
                list = trimmed.split(',');
            }
        } else {
            list = [raw];
        }

        const all = list
            .map((c) => (typeof c === 'string' ? c.trim() : c))
            .filter((c) => c !== null && c !== undefined && c !== '');

        return { primary: all.length > 0 ? all[0] : undefined, all };
    } catch {
        return { primary: undefined, all: [] };
    }
};

const isTest = process.env.NODE_ENV === 'test';
const isProduction = process.env.NODE_ENV === 'production';
const hasKey = !!process.env.REACT_APP_POSTHOG_KEY;
const devOptIn = process.env.REACT_APP_POSTHOG_ENABLE_ANALYTICS === 'true';

const shouldSendToPostHog = () => {
    if (isTest) return false;
    if (!hasKey) return false;
    if (!isProduction && !devOptIn) return false;
    return true;
};

let initialized = false;

const initAnalytics = () => {
    if (initialized || isTest) return;
    if (!hasKey) return;
    if (!isProduction && !devOptIn) {
        initialized = true;
        return;
    }

    try {
        posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
            api_host: process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com',
            // We emit explicit events; autocapture creates a lot of noise we don't want.
            autocapture: false,
            capture_pageview: false,
            capture_pageleave: true,
            persistence: 'localStorage+cookie',
            disable_session_recording: true,
            loaded: (ph) => {
                if (!isProduction) {
                    ph.debug();
                }
            },
        });
        initialized = true;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[Analytics] PostHog init failed', err);
    }
};

const getSessionId = () => {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) return undefined;
        const KEY = 'pearl_analytics_session_id';
        let id = window.sessionStorage.getItem(KEY);
        if (!id) {
            id =
                typeof crypto !== 'undefined' && crypto.randomUUID
                    ? crypto.randomUUID()
                    : `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
            window.sessionStorage.setItem(KEY, id);
        }
        return id;
    } catch {
        return undefined;
    }
};

const getBaseProperties = () => ({
    app: 'pearl-vendor-app',
    environment: process.env.NODE_ENV,
    timestamp: new Date().toISOString(),
    session_id: getSessionId(),
});

const trackEvent = (eventName, properties = {}) => {
    if (!eventName) return;

    const payload = {
        ...getBaseProperties(),
        ...properties,
    };

    try {
        if (shouldSendToPostHog()) {
            if (!initialized) initAnalytics();
            posthog.capture(eventName, payload);
        } else if (!isTest) {
            // eslint-disable-next-line no-console
            console.log('[Analytics - Dev]', eventName, payload);
        }
    } catch (err) {
        // Never let analytics break product flows.
        // eslint-disable-next-line no-console
        console.warn('[Analytics] trackEvent failed', eventName, err);
    }
};

const identifyUser = (userId, traits = {}) => {
    if (!userId) return;
    try {
        if (shouldSendToPostHog()) {
            if (!initialized) initAnalytics();
            posthog.identify(String(userId), traits);
        } else if (!isTest) {
            // eslint-disable-next-line no-console
            console.log('[Analytics - Dev] identify', userId, traits);
        }
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[Analytics] identifyUser failed', err);
    }
};

const resetUser = () => {
    try {
        if (shouldSendToPostHog() && initialized) {
            posthog.reset();
        }
    } catch {
        // ignore
    }
};

export {
    ANALYTICS_EVENTS,
    VENDOR_VIEW_SOURCES,
    normalizeVendorCategory,
    initAnalytics,
    trackEvent,
    identifyUser,
    resetUser,
    getSessionId,
    getBaseProperties,
};
